/**
 * Dialogue de confirmation.
 *
 * Exigence A-31 : **aucune action destructive sans confirmation explicite**. Le dialogue énumère
 * les conséquences réelles (ici, la publication clôt les runs en cours, §8.5) plutôt qu'un
 * « êtes-vous sûr ? » qui n'apprend rien.
 *
 * Deux protections contre le double-clic :
 * 1. le bouton de validation reçoit `enCours`, donc il est `disabled` dès le premier clic ;
 * 2. le parent verrouille son propre état (`enCours`) avant l'appel réseau — le second clic ne
 *    déclenche rien même si le rendu n'a pas encore eu lieu.
 */
import { useEffect, useRef } from 'react';

import { Bouton } from './Bouton';
import { M } from '../catalogue/messages';

export interface ProprietesDialogueConfirmation {
  readonly ouvert: boolean;
  readonly titre: string;
  readonly corps: string;
  readonly consequences?: readonly string[];
  readonly libelleConfirmer: string;
  readonly libelleAnnuler?: string;
  readonly ton?: 'danger' | 'principal';
  readonly enCours?: boolean;
  readonly surConfirmer: () => void;
  readonly surAnnuler: () => void;
}

export function DialogueConfirmation({
  ouvert,
  titre,
  corps,
  consequences,
  libelleConfirmer,
  libelleAnnuler = M.commun.annuler,
  ton = 'danger',
  enCours = false,
  surConfirmer,
  surAnnuler,
}: ProprietesDialogueConfirmation) {
  const referenceConfirmer = useRef<HTMLDivElement>(null);

  // Le focus part sur le bouton d'annulation : la touche Entrée ne publie pas par accident.
  useEffect(() => {
    if (!ouvert) return;
    const premier =
      referenceConfirmer.current?.querySelector<HTMLButtonElement>('.bouton--secondaire');
    premier?.focus();
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (evenement: KeyboardEvent): void => {
      if (evenement.key === 'Escape' && !enCours) surAnnuler();
    };
    window.addEventListener('keydown', surTouche);
    return () => {
      window.removeEventListener('keydown', surTouche);
    };
  }, [ouvert, enCours, surAnnuler]);

  if (!ouvert) return null;

  return (
    <div className="voile" role="presentation">
      <div
        className="dialogue"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialogue-titre"
        aria-describedby="dialogue-corps"
        ref={referenceConfirmer}
      >
        <h2 className="dialogue__titre" id="dialogue-titre">
          {titre}
        </h2>
        <p className="dialogue__corps" id="dialogue-corps">
          {corps}
        </p>
        {consequences === undefined ? null : (
          <ul className="dialogue__consequences">
            {consequences.map((consequence) => (
              <li key={consequence}>{consequence}</li>
            ))}
          </ul>
        )}
        <div className="dialogue__actions">
          <Bouton variante="secondaire" onClick={surAnnuler} disabled={enCours}>
            {libelleAnnuler}
          </Bouton>
          <Bouton variante={ton} onClick={surConfirmer} enCours={enCours}>
            {libelleConfirmer}
          </Bouton>
        </div>
      </div>
    </div>
  );
}
